import {
  Box,
  List,
  Alert,
  Stack,
  Divider,
  Skeleton,
  Snackbar,
  ListItem,
  SnackbarCloseReason,
} from "@mui/material";
import {
  SESSION_KEYS,
  SITE_TECHNICAL_DATA,
  UI_FLOW_PREFERENCES,
} from "src/consts/session";
import {
  hemProviders,
  setHemProvider,
  leadSelfOnboardingTransfer,
} from "src/api/selfOnboardingClients";
import { PvSetupTypes } from "../PvSetup";
import { WallboxTypes } from "../Wallbox";
import { ROUTES } from "src/consts/routes";
import { useTranslation } from "react-i18next";
import { SmartMeterTypes } from "../SmartMeter";
import Text from "src/components/Displays/Text";
import { BatterySetupTypes } from "../BatterySetup";
import { numberFormat } from "src/helpers/numberFormat";
import { useCallback, useEffect, useState } from "react";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { useNavigate, useSearchParams } from "react-router-dom";
import OemListItem from "src/components/SelfOnboarding/Components/OemListItem";

const Compatibility = () => {
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const theme = searchParams.get("theme");
  const { t } = useTranslation("translation");
  const transferId = searchParams.get("tid") || "";
  const salesPartnerUuid = searchParams.get("spid") || "";

  const [isLoading, setIsLoading] = useState({
    oems: true,
    setOem: false,
  });

  const [, setTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);
  const [, setOemSelected] = useSessionStorage<string>(
    SESSION_KEYS.SELF_ONBOARDING_OEM_SELECTED,
  );
  const [getSessionStorageItem] = useSessionStorage<string>(
    SESSION_KEYS.TRANSFER_ID,
  );
  const [, setUiFlowPreferences] = useSessionStorage<UI_FLOW_PREFERENCES>(
    SESSION_KEYS.UI_FLOW_PREFERENCES,
  );
  const [, setSiteTechnicalData] = useSessionStorage<SITE_TECHNICAL_DATA>(
    SESSION_KEYS.SITE_TECHNICAL_DATA,
  );

  const [allHemsProviders, setAllhemsProviders] = useState<
    {
      logicalId: string;
      displayName: string;
    }[]
  >([]);

  const initialCall = useCallback(async () => {
    try {
      const sessionTransferId = getSessionStorageItem();
      if (sessionTransferId && sessionTransferId === transferId) {
        const allHemsProviders = await hemProviders(transferId);

        setTimeout(() => {
          setAllhemsProviders(allHemsProviders.hemProviders);
          setIsLoading((prevValues) => ({
            ...prevValues,
            oems: false,
          }));
        }, 1000);

        return;
      }

      const response = await leadSelfOnboardingTransfer({
        transferId,
        salesPartnerUuid,
      });
      setTransferId(transferId);
      setUiFlowPreferences({
        language: response.uiFlowPreferences.language,
        callbackUrlSuccess: response.uiFlowPreferences.callbackUrlSuccess,
        callbackUrlFailure: response.uiFlowPreferences.callbackUrlFailure,
        processType: response.uiFlowPreferences.processType,
        skipOemAuth: response.uiFlowPreferences.skipOemAuth.toString(),
      });

      setSiteTechnicalData({
        pvSetup: {
          pvInstalled: response.siteTechnicalData.pvInstalled
            ? PvSetupTypes.JA
            : PvSetupTypes.NEIN,

          pvPowerMaxKwp: numberFormat(
            response.siteTechnicalData.pvPowerMaxKwp
              ? response.siteTechnicalData.pvPowerMaxKwp.toString()
              : "",
          ),
        },

        wallboxInstalled: response.siteTechnicalData.wallboxInstalled
          ? WallboxTypes.JA
          : WallboxTypes.NEIN,

        batterySetup: {
          batteryInstalled: response.siteTechnicalData.batteryInstalled
            ? BatterySetupTypes.JA
            : BatterySetupTypes.NEIN,

          batteryCapacity: numberFormat(
            response.siteTechnicalData.batterySizeKwh
              ? response.siteTechnicalData.batterySizeKwh.toString()
              : "",
          ),
        },

        smartMeterInstalled: response.siteTechnicalData.smartMeterInstalled
          ? SmartMeterTypes.JA
          : SmartMeterTypes.NEIN,
        heatpumpInstalled:
          response.siteTechnicalData.heatpumpInstalled?.toString(),
        marketLocationId: response.siteTechnicalData.marketLocationId,
        meterNumber: response.siteTechnicalData.meterNumber,
      });

      if (response.siteTechnicalData.hemProviderLogicalId) {
        setOemSelected(response.siteTechnicalData.hemProviderLogicalId);
        navigate(ROUTES.PV_SETUP);
      }

      const allHemsProviders = await hemProviders(transferId);

      setTimeout(() => {
        setAllhemsProviders(allHemsProviders.hemProviders);
        setIsLoading((prevValues) => ({
          ...prevValues,
          oems: false,
        }));
      }, 1000);
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialCall();
  }, [initialCall]);

  const handleNextClick = async (logicalId: string) => {
    try {
      setIsLoading((prevValues) => ({
        ...prevValues,
        setOem: true,
      }));
      await setHemProvider(transferId, logicalId);

      setTimeout(() => {
        setIsLoading((prevValues) => ({
          ...prevValues,
          setOem: false,
        }));

        navigate(
          `${ROUTES.INTRODUCTION}?tid=${transferId}&spid=${salesPartnerUuid}&theme=${theme}`,
        );
        setOemSelected(logicalId);
      }, 1000);
    } catch (e) {
      setMsg("Zur Zeit nicht verfügbar");
      setOpen(true);
      setIsLoading((prevValues) => ({
        ...prevValues,
        setOem: false,
      }));
    }
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  return (
    <Stack gap={5}>
      <Stack gap={5}>
        <Text
          text={t("page.selfOnboardingCompatibility.pageTitle")}
          sx={(theme) => ({
            fontWeight: 500,
            fontSize: "32px",
            lineHeight: "40px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Text
          text={t("page.selfOnboardingCompatibility.pageSubTitle")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />
      </Stack>

      <Stack gap={5}>
        <Text
          text={t("page.selfOnboardingCompatibility.formTitle")}
          sx={(theme) => ({
            fontSize: "24px",
            fontWeight: "500",
            lineHeight: "28px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Box
          sx={(theme) => ({
            px: "16px",
            borderRadius: "12px",
            bgcolor: theme.palette.white.main,
          })}
        >
          <List>
            {isLoading.oems && (
              <Skeleton
                sx={{
                  fontSize: "3rem",
                }}
              />
            )}

            {allHemsProviders &&
              allHemsProviders.map((hems, index) => (
                <>
                  <OemListItem
                    itemText={hems.displayName}
                    handleItemClick={() => handleNextClick(hems.logicalId)}
                    sx={{
                      pointerEvents: isLoading.setOem ? "none" : "auto",
                      opacity: isLoading.setOem ? 0.5 : 1,
                    }}
                  />
                  {allHemsProviders.length !== index + 1 && (
                    <Divider sx={{ ml: "16px", mr: "16px" }} />
                  )}
                </>
              ))}
            <ListItem disablePadding></ListItem>
          </List>
        </Box>
      </Stack>

      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          severity="warning"
          variant="filled"
          onClose={handleClose}
          sx={{ width: "100%" }}
        >
          {msg}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Compatibility;
